import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'react-toastify';
import './SendMessage.css';

const SendMessage = () => {
  const { user, updateCredits } = useAuth();
  const navigate = useNavigate();

  // Wizard step
  const [step, setStep] = useState(1);
  const totalSteps = 4;

  // Form data
  const [senderIds, setSenderIds] = useState([]);
  const [customSenderOption, setCustomSenderOption] = useState(null);
  const [customSenderUnlocked, setCustomSenderUnlocked] = useState(false);
  const [templates, setTemplates] = useState({});
  const [selectedSenderId, setSelectedSenderId] = useState('');
  const [customSender, setCustomSender] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [recipientPhone, setRecipientPhone] = useState('');
  const [content, setContent] = useState('');
  const [sendOption, setSendOption] = useState('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [senderRes, templateRes] = await Promise.all([
        api.get('/sms/sender-ids'),
        api.get('/sms/templates')
      ]);
      setSenderIds(senderRes.data.senderIds);
      setCustomSenderOption(senderRes.data.customSender);
      setCustomSenderUnlocked(senderRes.data.customSenderUnlocked);
      setTemplates(templateRes.data.templates);
      
      if (senderRes.data.senderIds.length > 0) {
        setSelectedSenderId(senderRes.data.senderIds[0].id);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load message options');
    } finally {
      setDataLoading(false);
    }
  };

  const handleSenderChange = (senderId) => {
    setSelectedSenderId(senderId);
    setSelectedTemplate(null);
    setContent('');
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setContent(template.content);
  };

  const getCharacterCount = () => {
    const length = content.length;
    const segments = Math.ceil(length / 160) || 1;
    return { length, segments };
  };

  const isCustomSenderSelected = selectedSenderId === 'Custom';

  const isCustomSenderValid = () => {
    if (!isCustomSenderSelected) return true;
    const value = customSender.trim();
    if (!value) return false;
    if (value.length > 11) return false;
    if (!/^[a-zA-Z0-9]+$/.test(value)) return false;
    return true;
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return selectedSenderId !== '' && isCustomSenderValid();
      case 2:
        return content.length > 0;
      case 3:
        return recipientPhone.length >= 10;
      case 4:
        return sendOption === 'now' || (scheduledDate && scheduledTime);
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (step < totalSteps && canProceed()) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    if (user?.credits < 1) {
      toast.error('No messages remaining. Please purchase more first.');
      navigate('/credits');
      return;
    }

    if (!recipientPhone || !content) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (isCustomSenderSelected && !isCustomSenderValid()) {
      toast.error('Please enter a valid custom sender ID');
      return;
    }

    setLoading(true);

    try {
      let response;
      const payload = {
        senderId: selectedSenderId,
        recipientPhone,
        content
      };

      if (isCustomSenderSelected) {
        payload.customSender = customSender.trim();
      }
      
      if (sendOption === 'now') {
        response = await api.post('/sms/send', payload);
      } else {
        const scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
        response = await api.post('/sms/schedule', {
          ...payload,
          scheduledAt
        });
      }

      updateCredits(response.data.creditsRemaining);
      toast.success(response.data.message);
      navigate('/history');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const { length, segments } = getCharacterCount();
  const charClass = length > 1600 ? 'danger' : length > 1440 ? 'warning' : '';

  if (dataLoading) {
    return (
      <div className="main-content">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  const selectedSender = isCustomSenderSelected
    ? customSenderOption
    : senderIds.find(s => s.id === selectedSenderId);

  const getDisplaySenderName = () => {
    if (isCustomSenderSelected) {
      return customSender.trim() || 'Custom Sender';
    }
    return selectedSender?.name;
  };

  return (
    <div className="main-content wizard-page">
      {/* Header */}
      <div className="wizard-header">
        <h1 className="wizard-title">✉️ Send a Message</h1>
        <p className="wizard-subtitle">Create and send a magical Christmas message</p>
        
        {/* Messages Check */}
        {user?.credits === 0 && (
          <div className="alert alert-error wizard-alert">
            <span>⚠️</span>
            <span>You need to purchase messages first!</span>
            <Link to="/credits" className="btn btn-gold btn-sm">
              Buy Credits
            </Link>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="wizard-progress">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${(step / totalSteps) * 100}%` }}
          ></div>
        </div>
        <div className="progress-steps">
          {[1, 2, 3, 4].map(s => (
            <div 
              key={s} 
              className={`progress-step ${step >= s ? 'active' : ''} ${step === s ? 'current' : ''}`}
              onClick={() => s < step && setStep(s)}
            >
              <div className="step-number">{s}</div>
              <div className="step-label">
                {s === 1 && 'Sender'}
                {s === 2 && 'Message'}
                {s === 3 && 'Recipient'}
                {s === 4 && 'Send'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Wizard Content */}
      <div className="wizard-content">
        {/* Step 1: Choose Sender */}
        {step === 1 && (
          <div className="wizard-step fade-in">
            <div className="step-icon">🎅</div>
            <h2 className="step-title">Who's sending this message?</h2>
            <p className="step-description">Choose a magical sender for your Christmas message</p>
            
            <div className="sender-grid">
              {senderIds.map(sender => (
                <div
                  key={sender.id}
                  className={`sender-card ${selectedSenderId === sender.id ? 'selected' : ''}`}
                  onClick={() => handleSenderChange(sender.id)}
                >
                  <div className="sender-emoji">{sender.emoji}</div>
                  <div className="sender-name">{sender.name}</div>
                  {selectedSenderId === sender.id && (
                    <div className="sender-check">✓</div>
                  )}
                </div>
              ))}

              {/* Custom Sender Option */}
              {customSenderUnlocked && customSenderOption && (
                <div
                  className={`sender-card custom-sender-card ${selectedSenderId === 'Custom' ? 'selected' : ''}`}
                  onClick={() => handleSenderChange('Custom')}
                >
                  <div className="sender-emoji">{customSenderOption.emoji}</div>
                  <div className="sender-name">{customSenderOption.name}</div>
                  {selectedSenderId === 'Custom' && (
                    <div className="sender-check">✓</div>
                  )}
                </div>
              )}
            </div>

            {/* Custom Sender Input */}
            {isCustomSenderSelected && (
              <div className="custom-sender-input-section fade-in">
                <label className="form-label">Your Custom Sender ID</label>
                <input
                  type="text"
                  className="form-input custom-sender-input"
                  value={customSender}
                  onChange={(e) => setCustomSender(e.target.value.toUpperCase())}
                  placeholder="e.g. MUM, DAD, FAMILY"
                  maxLength={11}
                />
                <p className="custom-sender-hint">
                  Up to 11 letters or numbers. No spaces or special characters.
                </p>
                {!isCustomSenderValid() && customSender.trim().length > 0 && (
                  <p className="custom-sender-error">
                    Please enter a valid sender ID (letters and numbers only, max 11 chars).
                  </p>
                )}
              </div>
            )}

            {!customSenderUnlocked && (
              <div className="unlock-sender-banner">
                <span>✨ Want to use your own sender name?</span>
                <Link to="/credits" className="btn btn-gold btn-sm">
                  Unlock for £10
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Message Content */}
        {step === 2 && (
          <div className="wizard-step fade-in">
            <div className="step-icon">💬</div>
            <h2 className="step-title">What would you like to say?</h2>
            <p className="step-description">Choose a template or write your own message</p>
            
            {/* Templates */}
            {!isCustomSenderSelected && (
              <div className="template-section">
                <h3 className="template-heading">📜 Quick Templates</h3>
                <div className="template-carousel">
                  {templates[selectedSenderId]?.map(template => (
                    <div
                      key={template.id}
                      className={`template-chip ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                      onClick={() => handleTemplateSelect(template)}
                    >
                      {template.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Message Input */}
            <div className="message-composer">
              <div className="composer-header">
                <span className="composer-from">
                  From: {isCustomSenderSelected ? '✏️' : selectedSender?.emoji} {getDisplaySenderName()}
                </span>
              </div>
              <textarea
                className="composer-textarea"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Ho Ho Ho! Write your magical Christmas message here..."
                rows={6}
                maxLength={1600}
              />
              <div className={`composer-footer ${charClass}`}>
                <span>{length} / 1600 characters</span>
                <span>{segments} SMS segment{segments > 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Recipient */}
        {step === 3 && (
          <div className="wizard-step fade-in">
            <div className="step-icon">📱</div>
            <h2 className="step-title">Who's receiving this magic?</h2>
            <p className="step-description">Enter the phone number of the lucky recipient</p>
            
            <div className="phone-input-container">
              <div className="phone-icon">📞</div>
              <input
                type="tel"
                className="phone-input"
                value={recipientPhone}
                onChange={(e) => setRecipientPhone(e.target.value)}
                placeholder="07123 456789"
              />
            </div>
            <p className="phone-hint">
              UK numbers starting with 07 will be automatically formatted. 
              For international numbers, include the country code (e.g., +1 for USA).
            </p>

            {/* Preview */}
            <div className="message-preview">
              <h3>📨 Message Preview</h3>
              <div className="preview-card">
                <div className="preview-header">
                  <span className="preview-from">
                    {isCustomSenderSelected ? '✏️' : selectedSender?.emoji} {getDisplaySenderName()}
                  </span>
                  <span className="preview-to">→ {recipientPhone || 'Recipient'}</span>
                </div>
                <div className="preview-content">
                  {content || 'Your message will appear here...'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Send Options */}
        {step === 4 && (
          <div className="wizard-step fade-in">
            <div className="step-icon">🚀</div>
            <h2 className="step-title">When should we send it?</h2>
            <p className="step-description">Send now or schedule for the perfect moment</p>
            
            <div className="send-options">
              <div 
                className={`send-option ${sendOption === 'now' ? 'selected' : ''}`}
                onClick={() => setSendOption('now')}
              >
                <div className="option-icon">⚡</div>
                <div className="option-content">
                  <div className="option-title">Send Now</div>
                  <div className="option-desc">Deliver instantly</div>
                </div>
                {sendOption === 'now' && <div className="option-check">✓</div>}
              </div>
              
              <div 
                className={`send-option ${sendOption === 'schedule' ? 'selected' : ''}`}
                onClick={() => setSendOption('schedule')}
              >
                <div className="option-icon">📅</div>
                <div className="option-content">
                  <div className="option-title">Schedule</div>
                  <div className="option-desc">Pick the perfect time</div>
                </div>
                {sendOption === 'schedule' && <div className="option-check">✓</div>}
              </div>
            </div>

            {sendOption === 'schedule' && (
              <div className="schedule-picker fade-in">
                <div className="picker-group">
                  <label>📅 Date</label>
                  <input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="picker-group">
                  <label>⏰ Time</label>
                  <input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="send-summary">
              <h3>📋 Summary</h3>
              <div className="summary-grid">
                <div className="summary-item">
                  <span className="summary-label">From</span>
                  <span className="summary-value">
                    {isCustomSenderSelected ? '✏️' : selectedSender?.emoji} {getDisplaySenderName()}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">To</span>
                  <span className="summary-value">{recipientPhone}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Message</span>
                  <span className="summary-value summary-message">{content.substring(0, 50)}{content.length > 50 ? '...' : ''}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Cost</span>
                  <span className="summary-value summary-cost">1 Credit</span>
                </div>
              </div>
              <div className="balance-info">
                Your balance: <strong>{user?.credits || 0}</strong> credits
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Wizard Navigation */}
      <div className="wizard-nav">
        {step > 1 && (
          <button 
            type="button" 
            className="btn btn-outline"
            onClick={prevStep}
          >
            ← Back
          </button>
        )}
        
        <div className="nav-spacer"></div>
        
        {step < totalSteps ? (
          <button 
            type="button" 
            className="btn btn-primary"
            onClick={nextStep}
            disabled={!canProceed()}
          >
            Next →
          </button>
        ) : (
          <button 
            type="button" 
            className="btn btn-gold btn-lg"
            onClick={handleSubmit}
            disabled={loading || user?.credits < 1 || !canProceed()}
          >
            {loading ? (
              '🎄 Sending...'
            ) : sendOption === 'now' ? (
              '🎅 Send Message'
            ) : (
              '📅 Schedule Message'
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default SendMessage;
